import React, { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { ReactComponent as Loader } from "../../../assests/logos/loader.svg";
import { useMsal } from "@azure/msal-react";

interface LoaderComponentProps {
  messages?: string[];
  interval?: number;
  error?: boolean;
  errorMsg?: string;
}

// Custom hook for timed messages
const useTimedMessages = (
  messages: string[],
  interval: number,
  isError: boolean
): string => {
  const [currentMessage, setCurrentMessage] = useState<string>(messages[0]);
  const currentIndex = useRef(0); // Persist index across renders

  useEffect(() => {
    if (isError) return; // Skip updates if there's an error

    const timer = setInterval(() => {
      currentIndex.current = (currentIndex.current + 1) % messages.length;
      setCurrentMessage(messages[currentIndex.current]);
    }, interval);

    // Cleanup interval on component unmount
    return () => clearInterval(timer);
  }, [messages, interval, isError]);

  return currentMessage;
};

export const LoaderComponent: React.FC<LoaderComponentProps> = ({
  messages = ["Please Wait, Loading..."],
  interval = 5000,
  error = false,
  errorMsg = "Oops! Something went wrong.",
}) => {
  const currentMessage = useTimedMessages(messages, interval, error);
  const { instance } = useMsal();
  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    instance.logoutRedirect();
  };
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="80vh"
      width="100%"
      textAlign="center"
    >
      {error ? (
        <>
          <Typography variant="h4" color="error" mb={2}>
            {errorMsg}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Please try again later or contact support. Click Here to{" "}
            <a
              onClick={logout}
              style={{
                cursor: "pointer",
                color: "primary.main",
                textDecoration: "underline",
              }}
            >
              Logout
            </a>
          </Typography>
        </>
      ) : (
        <>
          <Loader style={{ width: 50, height: 50 }} />
          <Typography variant="h6" mt={2} color="GrayText">
            {currentMessage}
          </Typography>
        </>
      )}
    </Box>
  );
};
